// ** React Imports
import { createContext, useState, ReactNode, useEffect, useContext } from "react";
import { useRouter } from "next/router";
import { UserContext, UserContextType } from "components/store";

// ** Types
interface AppStoreContextType {
  showPrintConfiguration: boolean;
  setShowPrintConfiguration: (showPrintConfiguration: boolean) => void;
  showPrintConfigurationModal: boolean;
  setShowPrintConfigurationModal: (showPrintConfigurationModal: boolean) => void;
  lastUsedTabs: LastUsedTab;
  setLastUsedTabs: (lastUsedTabs: LastUsedTab) => void;
  hideSidebar: boolean;
  setHideSidebar: (hideSidebar: boolean) => void;
  hideTopbar: boolean;
  setHideTopbar: (hideTopbar: boolean) => void;
  showingSuperAdminBanner: boolean;
  setShowingSuperAdminBanner: (showingSuperAdminBanner: boolean) => void;
  spaceLogoUrl: string | null;
  setSpaceLogoUrl: (spaceLogoUrl: string | null) => void;
}

interface AppStoreProviderProps {
  children: ReactNode;
}

interface LastUsedTab {
  [key: string]: string | number;
}

// ** Default values
const defaultStoreValues: AppStoreContextType = {
  showPrintConfiguration: false,
  setShowPrintConfiguration: (showPrintConfiguration: boolean) => null,
  showPrintConfigurationModal: false,
  setShowPrintConfigurationModal: (showPrintConfigurationModal: boolean) => null,
  lastUsedTabs: {},
  setLastUsedTabs: () => null,
  hideSidebar: false,
  setHideSidebar: () => null,
  hideTopbar: false,
  setHideTopbar: () => null,
  showingSuperAdminBanner: false,
  setShowingSuperAdminBanner: () => null,
  spaceLogoUrl: null,
  setSpaceLogoUrl: () => null,
};

// ** Create Context
const AppStoreContext = createContext<AppStoreContextType>(defaultStoreValues);

const AppStoreProvider = ({ children }: AppStoreProviderProps) => {
  //* State
  const [showPrintConfiguration, setShowPrintConfiguration] = useState<boolean>(defaultStoreValues.showPrintConfiguration);
  const [showPrintConfigurationModal, setShowPrintConfigurationModal] = useState<boolean>(defaultStoreValues.showPrintConfigurationModal);
  const [lastUsedTabs, setLastUsedTabs] = useState(defaultStoreValues.lastUsedTabs);
  const [hideSidebar, setHideSidebar] = useState<boolean>(true);
  const [hideTopbar, setHideTopbar] = useState<boolean>(true);
  const [showingSuperAdminBanner, setShowingSuperAdminBanner] = useState(defaultStoreValues.showingSuperAdminBanner);
  const [spaceLogoUrl, setSpaceLogoUrl] = useState<string | null>(defaultStoreValues.spaceLogoUrl);

  //* Context
  const { user } = useContext(UserContext) as UserContextType;

  const router = useRouter();

  useEffect(() => {
    if (!user?.id) {
      setShowPrintConfiguration(defaultStoreValues.showPrintConfiguration);
    }
  }, [user]);

  useEffect(() => {
    // every page change - hide printer configuration and when the page loads - it can be shown again on a page by page basis
    return () => {
      setShowPrintConfiguration(defaultStoreValues.showPrintConfiguration);
    };
  }, [router.route]);

  const values = {
    showPrintConfiguration,
    setShowPrintConfiguration,
    showPrintConfigurationModal,
    setShowPrintConfigurationModal,
    lastUsedTabs,
    setLastUsedTabs,
    hideSidebar,
    setHideSidebar,
    hideTopbar,
    setHideTopbar,
    showingSuperAdminBanner,
    setShowingSuperAdminBanner,
    spaceLogoUrl,
    setSpaceLogoUrl,
  };

  return <AppStoreContext.Provider value={values}>{children}</AppStoreContext.Provider>;
};

export { AppStoreContext, AppStoreProvider };
